import QRCode from 'qrcode'
import axiosInstance from './AxiosClient'

/**
 * Donor form access grants.
 *
 * A donor scanning a fundraiser's QR code lands on the form with no login, so the
 * request has to prove which fundraiser it belongs to. It carries a signed,
 * campaign-scoped token minted by the backend for the logged-in fundraiser rather
 * than that fundraiser's raw id, so a copied link expires instead of working
 * indefinitely.
 */

const FORM_TOKEN_ENDPOINT = '/donor-form-token/'
const STORAGE_KEY = 'donorFormToken'

/** Mints a grant for the signed-in fundraiser on `campaignId`. */
export async function requestDonorFormToken(campaignId) {
  const res = await axiosInstance.post(FORM_TOKEN_ENDPOINT, { campaign: campaignId })
  return res.data?.token ?? null
}

/**
 * The grant for the current donor session. Read from the URL on first load, then
 * from storage so it survives the multi-step form and a mid-form reload.
 */
export function resolveFormToken(route) {
  const fromUrl = route?.query?.form_token
  if (fromUrl) {
    storeFormToken(fromUrl)
    return fromUrl
  }
  return readStoredFormToken()
}

export function storeFormToken(token) {
  if (token) localStorage.setItem(STORAGE_KEY, token)
}

export function readStoredFormToken() {
  return localStorage.getItem(STORAGE_KEY) || null
}

export function clearStoredFormToken() {
  localStorage.removeItem(STORAGE_KEY)
}

/**
 * The credential a link handed to this page, if any.
 *
 * Verification texts sent before signed grants shipped carry a raw fundraiser id
 * instead of a token, and donors answer those texts over several days. The backend
 * only honours the raw id while ALLOW_LEGACY_FUNDRAISER_FORM_PARAM is on, so passing
 * it through keeps in-flight texts working across the release and stops working with
 * the rest of the legacy path.
 */
export function donorLinkCredential(route) {
  const formToken = route?.query?.form_token ?? null
  return {
    formToken,
    legacyFundraiser: formToken ? null : route?.query?.fundraiser ?? null,
  }
}

/** `?form_token=…` for anonymous donor-device calls, or '' when signed in. */
export function formTokenQuery(token, legacyFundraiser = null) {
  if (token) return `?form_token=${encodeURIComponent(token)}`
  return legacyFundraiser ? `?fundraiser=${encodeURIComponent(legacyFundraiser)}` : ''
}

/** Appends the grant to a URL that already has a query string. */
export function appendFormToken(params, token) {
  return token ? `${params}&form_token=${encodeURIComponent(token)}` : params
}

/**
 * Renders the QR code locally. The URL embeds a live credential, so it must not be
 * handed to an external image service to be drawn.
 */
export async function renderQrCodeDataUrl(url, size = 150) {
  return QRCode.toDataURL(url, { width: size, margin: 1, errorCorrectionLevel: 'M' })
}
